import React, { forwardRef, useRef, useState, useImperativeHandle, useEffect, useCallback } from "react";

/**
 * Lightweight canvas-based signature pad with modal expansion support.
 * Retains stroke drawing data without distortion when toggling between standard and fullscreen views.
 */
const SignaturePad = forwardRef(({ label, disabled, existingImageUrl, onRetake }, ref) => {
  const canvasRef = useRef(null);
  const modalCanvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);

  // Vector stroke points store: array of stroke arrays [{x: ratioX, y: ratioY}]
  const strokeHistory = useRef([]);
  const currentStroke = useRef([]);

  const [hasDrawn, setHasDrawn] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Redraw stored strokes onto a target canvas using relative ratio coordinates
  const redrawCanvas = useCallback((canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (strokeHistory.current.length === 0) return;

    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    strokeHistory.current.forEach((stroke) => {
      if (stroke.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x * canvas.width, stroke[0].y * canvas.height);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x * canvas.width, stroke[i].y * canvas.height);
      }
      ctx.stroke();
    });
  }, []);

  const syncActiveCanvas = useCallback(() => {
    if (isMaximized) {
      redrawCanvas(modalCanvasRef.current);
    } else {
      redrawCanvas(canvasRef.current);
    }
  }, [isMaximized, redrawCanvas]);

  // Sync canvas state on window resize or view transition
  useEffect(() => {
    const handleResize = () => {
      if (isMaximized && modalCanvasRef.current) {
        modalCanvasRef.current.width = modalCanvasRef.current.offsetWidth;
        modalCanvasRef.current.height = modalCanvasRef.current.offsetHeight;
      }
      syncActiveCanvas();
    };

    if (isMaximized && modalCanvasRef.current) {
      modalCanvasRef.current.width = modalCanvasRef.current.offsetWidth;
      modalCanvasRef.current.height = modalCanvasRef.current.offsetHeight;
      redrawCanvas(modalCanvasRef.current);
    } else {
      redrawCanvas(canvasRef.current);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMaximized, redrawCanvas, syncActiveCanvas]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
      ratioX: (clientX - rect.left) / rect.width,
      ratioY: (clientY - rect.top) / rect.height,
    };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    const canvas = isMaximized ? modalCanvasRef.current : canvasRef.current;
    const pos = getPos(e, canvas);
    lastPoint.current = pos;
    currentStroke.current = [{ x: pos.ratioX, y: pos.ratioY }];
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const canvas = isMaximized ? modalCanvasRef.current : canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);

    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    lastPoint.current = pos;
    currentStroke.current.push({ x: pos.ratioX, y: pos.ratioY });

    if (!hasDrawn) setHasDrawn(true);
  };

  const end = () => {
    if (drawing.current && currentStroke.current.length > 0) {
      strokeHistory.current.push(currentStroke.current);
      currentStroke.current = [];
    }
    drawing.current = false;
  };

  const clear = () => {
    strokeHistory.current = [];
    currentStroke.current = [];
    setHasDrawn(false);
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    if (modalCanvasRef.current) {
      const ctx = modalCanvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, modalCanvasRef.current.width, modalCanvasRef.current.height);
    }
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => !hasDrawn,

    clear,

    getBlob: () =>
      new Promise((resolve) => {
        const targetCanvas = isMaximized
          ? modalCanvasRef.current
          : canvasRef.current;

        targetCanvas.toBlob(
          (blob) => resolve(blob),
          "image/png"
        );
      }),

    toDataURL: () => {
      const targetCanvas = isMaximized
        ? modalCanvasRef.current
        : canvasRef.current;

      return targetCanvas
        ? targetCanvas.toDataURL("image/png")
        : existingImageUrl || "";
    },
  }));

  const toggleMaximize = (state) => {
    setIsMaximized(state);
  };

  return (
    <div style={{ border: "1px solid #ccc", borderRadius: 6, padding: 10, marginTop: 8, maxWidth: 420, background: "#fff" }}>
      {/* Header Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 13, color: "#555", fontWeight: 500 }}>{label}</div>
        {!existingImageUrl && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ padding: "1px 6px", fontSize: 12 }}
            onClick={() => toggleMaximize(true)}
            disabled={disabled}
            title="Expand Signature Pad"
          >
            ⛶ Maximize
          </button>
        )}
      </div>

      {existingImageUrl ? (
        <div style={{ textAlign: "center" }}>
          <img src={existingImageUrl} alt={label} style={{ maxHeight: 100, display: "inline-block" }} />
          {onRetake && (
            <div className="mt-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  clear();
                  onRetake();
                }}
                disabled={disabled}
              >
                Retake Signature
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Default inline Canvas */}
          <canvas
            ref={canvasRef}
            width={400}
            height={120}
            style={{
              width: "100%",
              maxWidth: 400,
              height: 120,
              background: "#fff",
              touchAction: "none",
              border: "1px dashed #bbb",
              borderRadius: 4,
              cursor: disabled ? "not-allowed" : "crosshair",
            }}
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
          />
          <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clear} disabled={disabled}>
              Clear
            </button>
          </div>
        </>
      )}

      {/* Fullscreen Modal View */}
      {isMaximized && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              width: "100%",
              maxWidth: 800,
              height: "80vh",
              display: "flex",
              flexDirection: "column",
              padding: 16,
              boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h6 style={{ margin: 0, fontWeight: 600 }}>{label || "Patient Signature"}</h6>
              <button
                type="button"
                className="btn-close"
                onClick={() => toggleMaximize(false)}
                aria-label="Close"
              />
            </div>

            <div style={{ flex: 1, position: "relative", width: "100%" }}>
              <canvas
                ref={modalCanvasRef}
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#fafafa",
                  touchAction: "none",
                  border: "2px dashed #999",
                  borderRadius: 6,
                  cursor: "crosshair",
                }}
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
              />
            </div>

            <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clear}>
                Clear / Reset
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => toggleMaximize(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default SignaturePad;
